import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { formatDateParam, parseDateParam } from '@/lib/utils/date-param';
import type { DateRange } from '@/components/shared/date-range-picker';
import type { ListAdminSubscriptionsParams } from '@/types/admin-subscription';

/**
 * Spec 410: Filter-Persistenz der Subscription-Liste über die URL (`useSearchParams`),
 * Mirror von `use-transaction-filter-params.ts` (#300/#380). Die Service/Operator/Status-
 * Filter sind Mehrfach+Ausschluss und liegen als `+/-`-Kommalisten unter
 * `service`/`operator`/`status` in der URL; `subId` ist eine Teilstringsuche, `renewals`
 * ein `<op>:<n>`-Vergleich (eq|gt). merchantId bleibt einwertig; Datum bleibt client-lokal.
 * Das BFF liest exakt diese Param-Namen (die Achsen-Kodierung macht `lib/utils/filter-axis`).
 *
 * Spec 380: zusätzlich „zuletzt genutzt" via localStorage-Snapshot. Die URL bleibt
 * Source-of-Truth; aus dem Snapshot wird nur vorbelegt, wenn die URL keine Filter trägt
 * (Deep-Links/Share gewinnen). Der Snapshot spiegelt jede URL-Änderung, leeren Zustand
 * eingeschlossen. Der Cursor wird nie persistiert (Restore → Seite 1).
 *
 * Migration: der Snapshot-Key ist `subscriptions.filters.v2`. Der alte v1-Snapshot der
 * Seite (bespoke localStorage in `pages/subscriptions/index.tsx`) trug einwertige
 * Facetten und wird ignoriert. Ein Legacy-`serviceId`-Deep-Link wird als einzelner Include
 * von `service` gelesen und beim nächsten Setzen verworfen.
 */

export const DEFAULT_SUB_LIMIT = 20;

const SUB_FILTERS_KEY = 'subscriptions.filters.v2';

// Kanonische Keys, die einen aktiven Filter markieren (Cursor liegt nicht in der URL).
const FILTER_KEYS = [
  'merchantId',
  'service',
  'status',
  'operator',
  'subId',
  'renewals',
  'limit',
  'dateFrom',
  'dateTo',
] as const;

/** Snapshot in URL-Form (alle Werte Strings, wie sie in der Query stehen). */
type SubFilterSnapshot = { [K in (typeof FILTER_KEYS)[number]]?: string };

function isSubFilterSnapshot(v: unknown): v is SubFilterSnapshot {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isNonDefaultLimit(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 && n !== DEFAULT_SUB_LIMIT;
}

/** Aktuellen URL-Filterzustand als Snapshot abbilden (nur vorhandene Filter-Keys). */
function subSnapshotFromParams(params: URLSearchParams): SubFilterSnapshot {
  const snap: SubFilterSnapshot = {};
  for (const key of FILTER_KEYS) {
    const v = params.get(key);
    if (v !== null && v !== '') snap[key] = v;
  }
  return snap;
}

/**
 * Snapshot → URLSearchParams mit Feld-Sanitisierung: ein ungültiges Feld fällt einzeln weg,
 * gültige seeden weiter. Die Mehrfach-Achsen (service/status/operator), subId und renewals
 * sind freie Strings — das BFF validiert/verwirft; hier reicht die Nicht-Leer-Prüfung.
 * Datums-/Limit-Werte werden streng geprüft.
 */
function buildSubSeedParams(s: SubFilterSnapshot): URLSearchParams {
  const p = new URLSearchParams();
  if (typeof s.merchantId === 'string' && s.merchantId) p.set('merchantId', s.merchantId);
  if (typeof s.service === 'string' && s.service) p.set('service', s.service);
  if (typeof s.status === 'string' && s.status) p.set('status', s.status);
  if (typeof s.operator === 'string' && s.operator) p.set('operator', s.operator);
  if (typeof s.subId === 'string' && s.subId) p.set('subId', s.subId);
  if (typeof s.renewals === 'string' && s.renewals) p.set('renewals', s.renewals);
  if (typeof s.dateFrom === 'string' && parseDateParam(s.dateFrom)) p.set('dateFrom', s.dateFrom);
  if (typeof s.dateTo === 'string' && parseDateParam(s.dateTo)) p.set('dateTo', s.dateTo);
  if (isNonDefaultLimit(s.limit)) p.set('limit', s.limit);
  return p;
}

function setOrDelete(params: URLSearchParams, key: string, value: string | undefined | null): void {
  if (value == null || value === '') params.delete(key);
  else params.set(key, value);
}

export interface UseSubscriptionFilterParams {
  /** Server-Filter inkl. limit (ohne cursor — der bleibt lokal in der Seite). */
  filters: ListAdminSubscriptionsParams;
  dateFrom?: Date;
  dateTo?: Date;
  /** True, sobald irgendein Filter (inkl. Nicht-Default-limit) gesetzt ist. */
  hasActiveFilters: boolean;
  /** Server-Filter setzen (cursor-Reset macht die Seite über den cursorStack). */
  setFilters: (next: ListAdminSubscriptionsParams) => void;
  setDateRange: (range: DateRange) => void;
  /** Alle Filter löschen → nackte URL `/subscriptions`, limit zurück auf Default. */
  reset: () => void;
}

export function useSubscriptionFilterParams(): UseSubscriptionFilterParams {
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshot, setSnapshot] = useLocalStorage<SubFilterSnapshot>(
    SUB_FILTERS_KEY,
    {},
    isSubFilterSnapshot,
  );

  const filters = useMemo<ListAdminSubscriptionsParams>(() => {
    const limitRaw = Number(searchParams.get('limit'));
    const limit = Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : DEFAULT_SUB_LIMIT;
    // Legacy read-compat: `?serviceId=svc_a` → ein einzelner Include von `service`.
    const legacyServiceId = searchParams.get('serviceId');
    const service =
      searchParams.get('service') ?? (legacyServiceId ? `+${legacyServiceId}` : undefined);
    return {
      merchantId: searchParams.get('merchantId') ?? undefined,
      service,
      statuses: searchParams.get('status') ?? undefined,
      operator: searchParams.get('operator') ?? undefined,
      subId: searchParams.get('subId') ?? undefined,
      renewals: searchParams.get('renewals') ?? undefined,
      limit,
    };
  }, [searchParams]);

  const dateFrom = useMemo(() => parseDateParam(searchParams.get('dateFrom')), [searchParams]);
  const dateTo = useMemo(() => parseDateParam(searchParams.get('dateTo')), [searchParams]);

  // Legacy `serviceId` zählt mit, damit ein Deep-Link nicht vom Snapshot überschrieben wird.
  const hasActiveFilters =
    FILTER_KEYS.some((key) => searchParams.has(key)) || searchParams.has('serviceId');

  // Spec 380: zwei Refs schützen die zwei bekannten Effekt-Fallen.
  const seededRef = useRef(false);
  const skipNextMirrorRef = useRef(false);

  // Seed-on-mount: leere URL + nicht-leerer (sanitisierter) Snapshot → URL vorbelegen.
  // Läuft genau einmal (seededRef); URL mit Filtern gewinnt (Deep-Link/Share).
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (hasActiveFilters) return;
    const seeded = buildSubSeedParams(snapshot);
    if (Array.from(seeded.keys()).length > 0) {
      skipNextMirrorRef.current = true; // den vom Seed ausgelösten Mirror überspringen
      setSearchParams(seeded, { replace: true });
    }
  }, [hasActiveFilters, snapshot, setSearchParams]);

  // Spiegeln: jede URL-Änderung (Setter ODER extern: Back/Forward, Deep-Link) in den
  // Snapshot, leeren Zustand eingeschlossen → kein stale Reseed.
  useEffect(() => {
    if (!seededRef.current) return; // erst nach der Seed-Entscheidung spiegeln
    if (skipNextMirrorRef.current) {
      skipNextMirrorRef.current = false;
      return;
    }
    setSnapshot(subSnapshotFromParams(searchParams));
  }, [searchParams, setSnapshot]);

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setFilters = useCallback(
    (next: ListAdminSubscriptionsParams) => {
      update((params) => {
        setOrDelete(params, 'merchantId', next.merchantId);
        setOrDelete(params, 'service', next.service);
        // Mehrfach-Status liegt unter dem URL-Key `status` (BFF-Param-Name).
        setOrDelete(params, 'status', next.statuses);
        setOrDelete(params, 'operator', next.operator);
        setOrDelete(params, 'subId', next.subId);
        setOrDelete(params, 'renewals', next.renewals);
        // Legacy-Param aufräumen, sobald irgendein Filter gesetzt wird.
        params.delete('serviceId');
        // limit nur persistieren, wenn != Default — hält Reset → /subscriptions sauber.
        if (next.limit && next.limit !== DEFAULT_SUB_LIMIT) params.set('limit', String(next.limit));
        else params.delete('limit');
        // Server-Filter-Wechsel resettet die Blätterung; stale `cursor` defensiv entfernen.
        params.delete('cursor');
      });
    },
    [update],
  );

  const setDateRange = useCallback(
    (range: DateRange) =>
      update((params) => {
        setOrDelete(params, 'dateFrom', range.from ? formatDateParam(range.from) : undefined);
        setOrDelete(params, 'dateTo', range.to ? formatDateParam(range.to) : undefined);
      }),
    [update],
  );

  // Spec 380 §4.3: Reset leert URL UND Snapshot — nach Reset seedet das nächste Mount nichts Stale.
  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams());
    setSnapshot({});
  }, [setSearchParams, setSnapshot]);

  return {
    filters,
    dateFrom,
    dateTo,
    hasActiveFilters,
    setFilters,
    setDateRange,
    reset,
  };
}
